import { useState, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    ArrowLeft, Phone, Calendar, Banknote, Check, X, Edit, Save, Trash2,
    MapPin, User, Shield, Clock, CalendarDays, Building2, FileText, Download, Paperclip
} from 'lucide-react';
import { Card, CardStats, Button, ConfirmModal, ExportDropdown, StatusBadge, DocumentUpload } from '@components/ui';
import { useExportExcel, useExportPDF } from '@hooks';
import { getPersonnelById, getRoleLabel, getRoleColor, mockAttendance, personnelRoles } from '@mocks/personel';
import { getPuantajByPersonel, calismaDurumlari } from '@mocks/puantaj';
import clsx from 'clsx';

// Çalışma günleri tanımı
const WORK_DAYS = [
    { key: 'mon', label: 'Pzt' },
    { key: 'tue', label: 'Sal' },
    { key: 'wed', label: 'Çar' },
    { key: 'thu', label: 'Per' },
    { key: 'fri', label: 'Cum' },
    { key: 'sat', label: 'Cmt' },
    { key: 'sun', label: 'Paz' },
];

export default function PersonelDetay() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [isEditing, setIsEditing] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('bilgiler');
    const [documents, setDocuments] = useState([
        { id: 'doc-1', name: 'TC_Kimlik_Fotokopisi.pdf', type: 'application/pdf', size: 512000, date: '2025-01-15', category: 'TC Fotokopisi' },
        { id: 'doc-2', name: 'SGK_Belgesi.pdf', type: 'application/pdf', size: 820000, date: '2025-01-16', category: 'SGK Belgesi' },
    ]);

    const person = getPersonnelById(id);

    const [formData, setFormData] = useState({
        name: person?.name || '',
        phone: person?.phone || '',
        role: person?.role || 'worker',
        dailyWage: person?.dailyWage?.toString() || '',
        startDate: person?.startDate || '',
        tcNo: person?.tcNo || '12345678901',
        address: person?.address || 'İstanbul, Türkiye',
        emergencyContact: person?.emergencyContact || '',
        // Sigorta bilgileri
        sgkNo: person?.sgkNo || 'SGK-2025-' + id,
        insuranceStartDate: person?.insuranceStartDate || person?.startDate || '',
        insuranceActive: person?.insuranceActive ?? true,
        // Çalışma programı
        workDays: person?.workDays || ['mon', 'tue', 'wed', 'thu', 'fri', 'sat'],
        workStartTime: person?.workStartTime || '08:00',
        workEndTime: person?.workEndTime || '17:00',
        breakDuration: person?.breakDuration || 60,
    });

    if (!person) {
        return (
            <div className="p-6 text-center">
                <p className="text-text-secondary">Personel bulunamadı</p>
                <button onClick={() => navigate('/insaat/personel')} className="mt-4 text-primary hover:underline">
                    Geri Dön
                </button>
            </div>
        );
    }

    // Export
    const reportRef = useRef(null);
    const { exportToExcel, isExporting: isExportingExcel } = useExportExcel();
    const { exportToPDF, isExporting: isExportingPDF } = useExportPDF();

    // Yoklama geçmişi - son 30 gün
    const attendanceHistory = useMemo(() => {
        const days = [];
        const today = new Date();
        for (let i = 29; i >= 0; i--) {
            const date = new Date(today);
            date.setDate(date.getDate() - i);
            const dateStr = date.toISOString().split('T')[0];
            const dayAttendance = mockAttendance[dateStr];
            days.push({
                date: dateStr,
                dayOfWeek: date.getDay(),
                isPresent: dayAttendance ? dayAttendance[id] : null,
            });
        }
        return days;
    }, [id]);

    // İstatistikler
    const stats = useMemo(() => {
        const workedDays = attendanceHistory.filter(d => d.isPresent === true).length;
        const absentDays = attendanceHistory.filter(d => d.isPresent === false).length;
        const totalTracked = workedDays + absentDays;
        const attendanceRate = totalTracked > 0 ? Math.round((workedDays / totalTracked) * 100) : 0;
        const dailyHours = calculateDailyHours();
        const monthlyHours = workedDays * dailyHours;

        return { workedDays, absentDays, attendanceRate, dailyHours, monthlyHours };
    }, [attendanceHistory, formData]);

    function calculateDailyHours() {
        const [startH, startM] = formData.workStartTime.split(':').map(Number);
        const [endH, endM] = formData.workEndTime.split(':').map(Number);
        const totalMinutes = (endH * 60 + endM) - (startH * 60 + startM) - formData.breakDuration;
        return Math.max(0, totalMinutes / 60);
    }

    const handleExportExcel = () => {
        exportToExcel({
            data: attendanceHistory.filter(d => d.isPresent !== null).map(d => ({
                'Tarih': d.date,
                'Durum': d.isPresent ? 'Geldi' : 'Gelmedi',
            })),
            columns: [
                { header: 'Tarih', key: 'Tarih' },
                { header: 'Durum', key: 'Durum' },
            ],
            fileName: `personel_yoklama_${person.name}`,
            sheetName: 'Yoklama',
        });
    };

    const handleExportPDF = () => {
        exportToPDF({
            elementRef: reportRef,
            fileName: `personel_${person.name}`,
            title: `Personel Kartı - ${person.name}`,
            orientation: 'portrait',
        });
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const toggleWorkDay = (dayKey) => {
        setFormData(prev => ({
            ...prev,
            workDays: prev.workDays.includes(dayKey)
                ? prev.workDays.filter(d => d !== dayKey)
                : [...prev.workDays, dayKey]
        }));
    };

    const handleSave = () => {
        console.log('Kaydedildi:', formData);
        toast.success('Personel bilgileri güncellendi');
        setIsEditing(false);
    };

    const handleCancel = () => {
        setFormData({
            name: person?.name || '',
            phone: person?.phone || '',
            role: person?.role || 'worker',
            dailyWage: person?.dailyWage?.toString() || '',
            startDate: person?.startDate || '',
            tcNo: person?.tcNo || '12345678901',
            address: person?.address || 'İstanbul, Türkiye',
            emergencyContact: person?.emergencyContact || '',
            sgkNo: person?.sgkNo || 'SGK-2025-' + id,
            insuranceStartDate: person?.insuranceStartDate || person?.startDate || '',
            insuranceActive: person?.insuranceActive ?? true,
            workDays: person?.workDays || ['mon', 'tue', 'wed', 'thu', 'fri', 'sat'],
            workStartTime: person?.workStartTime || '08:00',
            workEndTime: person?.workEndTime || '17:00',
            breakDuration: person?.breakDuration || 60,
        });
        setIsEditing(false);
    };

    const confirmDelete = () => {
        toast.success(`${person.name} silindi`);
        setDeleteModalOpen(false);
        navigate('/insaat/personel');
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('tr-TR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        });
    };

    const inputClass = "w-full px-4 py-3 rounded-lg border border-border-light bg-bg-primary text-text-primary focus:ring-2 focus:ring-primary focus:border-transparent";
    const labelClass = "block text-sm font-medium text-text-secondary mb-1";

    return (
        <div className="p-4 md:p-6 space-y-6" ref={reportRef}>
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/insaat/personel')}
                        className="p-2 hover:bg-bg-hover rounded-lg transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                    >
                        <ArrowLeft size={20} className="text-text-secondary" />
                    </button>
                    <div>
                        <h1 className="text-xl sm:text-2xl font-bold text-text-primary">{person.name}</h1>
                        <span className={clsx(
                            "inline-flex px-2 py-0.5 rounded-full text-xs font-medium text-white mt-1",
                            getRoleColor(person.role)
                        )}>
                            {getRoleLabel(person.role)}
                        </span>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <ExportDropdown
                        onExportExcel={handleExportExcel}
                        onExportPDF={handleExportPDF}
                        isExporting={isExportingExcel || isExportingPDF}
                    />
                    {isEditing ? (
                        <>
                            <Button variant="ghost" onClick={handleCancel} icon={<X size={18} />}>
                                İptal
                            </Button>
                            <Button variant="success" onClick={handleSave} icon={<Save size={18} />}>
                                Kaydet
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button variant="ghost-error" onClick={() => setDeleteModalOpen(true)} icon={<Trash2 size={18} />}>
                                <span className="hidden sm:inline">Sil</span>
                            </Button>
                            <Button variant="primary" onClick={() => setIsEditing(true)} icon={<Edit size={18} />}>
                                Düzenle
                            </Button>
                        </>
                    )}
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                <CardStats
                    title="Günlük Ücret"
                    value={`₺${Number(formData.dailyWage).toLocaleString('tr-TR')}`}
                    icon={Banknote}
                    color="success"
                    size="sm"
                />
                <CardStats
                    title="Devam Oranı"
                    value={`%${stats.attendanceRate}`}
                    icon={Calendar}
                    color={stats.attendanceRate >= 80 ? 'success' : stats.attendanceRate >= 50 ? 'warning' : 'error'}
                    size="sm"
                />
                <CardStats
                    title="Çalışılan Gün"
                    value={stats.workedDays}
                    icon={CalendarDays}
                    color="primary"
                    size="sm"
                />
                <CardStats
                    title="Günlük Mesai"
                    value={`${stats.dailyHours.toFixed(1)} saat`}
                    icon={Clock}
                    color="secondary"
                    size="sm"
                />
                <CardStats
                    title="Aylık Mesai"
                    value={`${stats.monthlyHours.toFixed(0)} saat`}
                    icon={Clock}
                    color="info"
                    size="sm"
                />
            </div>

            {/* Tabs */}
            <div className="flex border-b border-border-light overflow-x-auto">
                {[
                    { key: 'bilgiler', label: 'Kişisel Bilgiler', icon: User },
                    { key: 'mesai', label: 'Çalışma Programı', icon: Clock },
                    { key: 'sigorta', label: 'Sigorta', icon: Shield },
                    { key: 'yoklama', label: 'Yoklama Geçmişi', icon: Calendar },
                    { key: 'puantaj', label: 'Puantaj Geçmişi', icon: CalendarDays },
                    { key: 'belgeler', label: 'Belgeler', icon: FileText },
                ].map(tab => (
                    <button
                        key={tab.key}
                        onClick={() => setActiveTab(tab.key)}
                        className={clsx(
                            "flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap",
                            activeTab === tab.key
                                ? "border-primary text-primary"
                                : "border-transparent text-text-muted hover:text-text-primary"
                        )}
                    >
                        <tab.icon size={16} />
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Tab Content */}
            {activeTab === 'bilgiler' && (
                <Card padding="lg">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {isEditing ? (
                            <>
                                <div>
                                    <label className={labelClass}>Ad Soyad</label>
                                    <input
                                        type="text"
                                        className={inputClass}
                                        value={formData.name}
                                        onChange={(e) => handleChange('name', e.target.value)}
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>Telefon</label>
                                    <input
                                        type="tel"
                                        className={inputClass}
                                        value={formData.phone}
                                        onChange={(e) => handleChange('phone', e.target.value)}
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>Rol</label>
                                    <select
                                        className={inputClass}
                                        value={formData.role}
                                        onChange={(e) => handleChange('role', e.target.value)}
                                    >
                                        {personnelRoles.map(role => (
                                            <option key={role.key} value={role.key}>{role.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className={labelClass}>Günlük Ücret (₺)</label>
                                    <input
                                        type="number"
                                        className={inputClass}
                                        value={formData.dailyWage}
                                        onChange={(e) => handleChange('dailyWage', e.target.value)}
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>TC Kimlik No</label>
                                    <input
                                        type="text"
                                        className={inputClass}
                                        value={formData.tcNo}
                                        maxLength={11}
                                        onChange={(e) => handleChange('tcNo', e.target.value)}
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>İşe Başlama Tarihi</label>
                                    <input
                                        type="date"
                                        className={inputClass}
                                        value={formData.startDate}
                                        onChange={(e) => handleChange('startDate', e.target.value)}
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <label className={labelClass}>Adres</label>
                                    <input
                                        type="text"
                                        className={inputClass}
                                        value={formData.address}
                                        onChange={(e) => handleChange('address', e.target.value)}
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <label className={labelClass}>Acil Durumda İletişim</label>
                                    <input
                                        type="text"
                                        className={inputClass}
                                        value={formData.emergencyContact}
                                        placeholder="İsim ve telefon"
                                        onChange={(e) => handleChange('emergencyContact', e.target.value)}
                                    />
                                </div>
                            </>
                        ) : (
                            <>
                                <InfoCard icon={Phone} label="Telefon" value={person.phone} />
                                <InfoCard icon={Calendar} label="İşe Başlama" value={formatDate(person.startDate)} />
                                <InfoCard icon={User} label="TC Kimlik No" value={formData.tcNo} />
                                <InfoCard icon={Banknote} label="Günlük Ücret" value={`₺${person.dailyWage.toLocaleString('tr-TR')}`} />
                                <InfoCard icon={MapPin} label="Adres" value={formData.address} className="md:col-span-2" />
                                <InfoCard icon={Phone} label="Acil Durum İletişim" value={formData.emergencyContact || 'Belirtilmemiş'} className="md:col-span-2" />
                            </>
                        )}
                    </div>
                </Card>
            )}

            {activeTab === 'mesai' && (
                <Card padding="lg">
                    <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
                        <Clock size={20} />
                        Çalışma Programı
                    </h3>

                    {isEditing ? (
                        <div className="space-y-6">
                            {/* Çalışma Günleri */}
                            <div>
                                <label className={labelClass}>Çalışma Günleri</label>
                                <div className="flex flex-wrap gap-2 mt-2">
                                    {WORK_DAYS.map(day => (
                                        <button
                                            key={day.key}
                                            type="button"
                                            onClick={() => toggleWorkDay(day.key)}
                                            className={clsx(
                                                "w-12 h-12 rounded-lg font-medium transition-all",
                                                formData.workDays.includes(day.key)
                                                    ? "bg-primary text-white"
                                                    : "bg-bg-tertiary text-text-muted hover:bg-bg-hover"
                                            )}
                                        >
                                            {day.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Çalışma Saatleri */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className={labelClass}>Başlangıç Saati</label>
                                    <input
                                        type="time"
                                        className={inputClass}
                                        value={formData.workStartTime}
                                        onChange={(e) => handleChange('workStartTime', e.target.value)}
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>Bitiş Saati</label>
                                    <input
                                        type="time"
                                        className={inputClass}
                                        value={formData.workEndTime}
                                        onChange={(e) => handleChange('workEndTime', e.target.value)}
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>Mola Süresi (dk)</label>
                                    <input
                                        type="number"
                                        className={inputClass}
                                        value={formData.breakDuration}
                                        onChange={(e) => handleChange('breakDuration', parseInt(e.target.value) || 0)}
                                    />
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {/* Çalışma Günleri Görünümü */}
                            <div>
                                <p className="text-sm text-text-muted mb-3">Çalışma Günleri</p>
                                <div className="flex flex-wrap gap-2">
                                    {WORK_DAYS.map(day => (
                                        <div
                                            key={day.key}
                                            className={clsx(
                                                "w-12 h-12 rounded-lg flex items-center justify-center font-medium",
                                                formData.workDays.includes(day.key)
                                                    ? "bg-success/10 text-success"
                                                    : "bg-bg-tertiary text-text-muted"
                                            )}
                                        >
                                            {formData.workDays.includes(day.key) ? <Check size={18} /> : day.label}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Çalışma Saatleri Özeti */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <InfoCard icon={Clock} label="Mesai Başlangıcı" value={formData.workStartTime} />
                                <InfoCard icon={Clock} label="Mesai Bitişi" value={formData.workEndTime} />
                                <InfoCard icon={Clock} label="Günlük Çalışma" value={`${stats.dailyHours.toFixed(1)} saat`} />
                            </div>

                            {/* Haftalık Özet */}
                            <div className="bg-bg-tertiary rounded-lg p-4">
                                <div className="flex items-center justify-between">
                                    <span className="text-text-secondary">Haftalık Çalışma</span>
                                    <span className="text-lg font-bold text-text-primary">
                                        {(formData.workDays.length * stats.dailyHours).toFixed(0)} saat
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}
                </Card>
            )}

            {activeTab === 'sigorta' && (
                <Card padding="lg">
                    <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
                        <Shield size={20} />
                        Sigorta Bilgileri
                    </h3>

                    {isEditing ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className={labelClass}>SGK Sicil No</label>
                                <input
                                    type="text"
                                    className={inputClass}
                                    value={formData.sgkNo}
                                    onChange={(e) => handleChange('sgkNo', e.target.value)}
                                />
                            </div>
                            <div>
                                <label className={labelClass}>Sigorta Başlangıç</label>
                                <input
                                    type="date"
                                    className={inputClass}
                                    value={formData.insuranceStartDate}
                                    onChange={(e) => handleChange('insuranceStartDate', e.target.value)}
                                />
                            </div>
                            <div>
                                <label className={labelClass}>Sigorta Durumu</label>
                                <select
                                    className={inputClass}
                                    value={formData.insuranceActive ? 'active' : 'inactive'}
                                    onChange={(e) => handleChange('insuranceActive', e.target.value === 'active')}
                                >
                                    <option value="active">Aktif</option>
                                    <option value="inactive">Pasif</option>
                                </select>
                            </div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <InfoCard icon={Shield} label="SGK Sicil No" value={formData.sgkNo} />
                            <InfoCard icon={Calendar} label="Sigorta Başlangıç" value={formatDate(formData.insuranceStartDate)} />
                            <div className={clsx(
                                "flex items-center gap-3 p-4 rounded-lg md:col-span-2",
                                formData.insuranceActive ? "bg-success/10" : "bg-error/10"
                            )}>
                                <div className={clsx(
                                    "w-10 h-10 rounded-lg flex items-center justify-center",
                                    formData.insuranceActive ? "bg-success" : "bg-error"
                                )}>
                                    {formData.insuranceActive ? <Check size={20} className="text-white" /> : <X size={20} className="text-white" />}
                                </div>
                                <div>
                                    <p className="font-medium text-text-primary">
                                        Sigorta {formData.insuranceActive ? 'Aktif' : 'Pasif'}
                                    </p>
                                    <p className="text-sm text-text-muted">
                                        {formData.insuranceActive ? 'Primler düzenli ödeniyor' : 'Sigorta kaydı bulunmuyor'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </Card>
            )}

            {activeTab === 'yoklama' && (
                <Card padding="lg">
                    <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
                        <Calendar size={20} />
                        Son 30 Gün Yoklama Geçmişi
                    </h3>

                    {/* Takvim Grid */}
                    <div className="grid grid-cols-7 gap-2 mb-6">
                        {/* Gün başlıkları */}
                        {['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'].map(day => (
                            <div key={day} className="text-center text-xs font-medium text-text-muted py-2">
                                {day}
                            </div>
                        ))}

                        {/* Güner */}
                        {attendanceHistory.map((day, idx) => {
                            const dayNum = new Date(day.date).getDate();
                            return (
                                <div
                                    key={day.date}
                                    className={clsx(
                                        "aspect-square rounded-lg flex flex-col items-center justify-center text-sm",
                                        day.isPresent === true && "bg-success/10 text-success",
                                        day.isPresent === false && "bg-error/10 text-error",
                                        day.isPresent === null && "bg-bg-tertiary text-text-muted"
                                    )}
                                    title={day.date}
                                >
                                    <span className="font-medium">{dayNum}</span>
                                    {day.isPresent !== null && (
                                        <span className="text-xs">
                                            {day.isPresent ? '✓' : '✗'}
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Özet */}
                    <div className="grid grid-cols-3 gap-4">
                        <div className="bg-success/10 rounded-lg p-4 text-center">
                            <p className="text-2xl font-bold text-success">{stats.workedDays}</p>
                            <p className="text-sm text-text-muted">Çalışılan Gün</p>
                        </div>
                        <div className="bg-error/10 rounded-lg p-4 text-center">
                            <p className="text-2xl font-bold text-error">{stats.absentDays}</p>
                            <p className="text-sm text-text-muted">Devamsız</p>
                        </div>
                        <div className="bg-primary/10 rounded-lg p-4 text-center">
                            <p className="text-2xl font-bold text-primary">%{stats.attendanceRate}</p>
                            <p className="text-sm text-text-muted">Devam Oranı</p>
                        </div>
                    </div>
                </Card>
            )}

            {activeTab === 'puantaj' && (() => {
                const puantajData = getPuantajByPersonel(id);
                const getDurumBadge = (durum) => {
                    const d = calismaDurumlari.find(c => c.key === durum);
                    return d || { key: durum, label: durum, color: 'neutral' };
                };
                const toplamSaat = puantajData.reduce((a, k) => a + k.calismaSaat, 0);
                const toplamFazlaMesai = puantajData.reduce((a, k) => a + k.fazlaMesai, 0);
                const calistiGun = puantajData.filter(k => k.durum === 'calisti' || k.durum === 'yarim_gun').length;

                return (
                    <Card padding="lg">
                        <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
                            <CalendarDays size={20} />
                            Puantaj Geçmişi
                        </h3>

                        {/* Özet Kartları */}
                        <div className="grid grid-cols-3 gap-4 mb-6">
                            <div className="bg-success/10 rounded-lg p-4 text-center">
                                <p className="text-2xl font-bold text-success">{calistiGun}</p>
                                <p className="text-sm text-text-muted">Çalışılan Gün</p>
                            </div>
                            <div className="bg-primary/10 rounded-lg p-4 text-center">
                                <p className="text-2xl font-bold text-primary">{toplamSaat.toFixed(1)} saat</p>
                                <p className="text-sm text-text-muted">Toplam Saat</p>
                            </div>
                            <div className="bg-warning/10 rounded-lg p-4 text-center">
                                <p className="text-2xl font-bold text-warning">+{toplamFazlaMesai.toFixed(1)} saat</p>
                                <p className="text-sm text-text-muted">Fazla Mesai</p>
                            </div>
                        </div>

                        {puantajData.length > 0 ? (
                            <>
                                {/* Mobile Cards */}
                                <div className="md:hidden space-y-3">
                                    {puantajData.map(k => {
                                        const durum = getDurumBadge(k.durum);
                                        return (
                                            <div key={k.id} className="bg-bg-tertiary rounded-lg p-4">
                                                <div className="flex items-center justify-between mb-2">
                                                    <span className="text-sm font-medium text-text-primary">
                                                        {new Date(k.tarih).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', weekday: 'short' })}
                                                    </span>
                                                    <StatusBadge variant={durum.color} status={durum.label} />
                                                </div>
                                                {(k.durum === 'calisti' || k.durum === 'yarim_gun') && (
                                                    <div className="grid grid-cols-3 gap-2 text-center text-sm">
                                                        <div><span className="text-text-muted">Mesai</span><br /><span className="font-medium">{k.baslangic}-{k.bitis}</span></div>
                                                        <div><span className="text-text-muted">Saat</span><br /><span className="font-medium">{k.calismaSaat.toFixed(1)} saat</span></div>
                                                        <div><span className="text-text-muted">F.Mesai</span><br /><span className={clsx("font-medium", k.fazlaMesai > 0 ? "text-warning" : "text-text-muted")}>{k.fazlaMesai > 0 ? `+${k.fazlaMesai.toFixed(1)} saat` : '-'}</span></div>
                                                    </div>
                                                )}
                                                {k.not && <p className="text-xs text-text-muted mt-2 italic">Not: {k.not}</p>}
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Desktop Table */}
                                <div className="hidden md:block overflow-x-auto">
                                    <table className="w-full text-left">
                                        <thead>
                                            <tr className="bg-bg-tertiary border-b border-border-light">
                                                <th className="py-3 px-4 text-xs font-semibold text-text-secondary">Tarih</th>
                                                <th className="py-3 px-4 text-xs font-semibold text-text-secondary text-center">Durum</th>
                                                <th className="py-3 px-4 text-xs font-semibold text-text-secondary text-center">Mesai</th>
                                                <th className="py-3 px-4 text-xs font-semibold text-text-secondary text-center">Çalışma</th>
                                                <th className="py-3 px-4 text-xs font-semibold text-text-secondary text-center">F.Mesai</th>
                                                <th className="py-3 px-4 text-xs font-semibold text-text-secondary">Not</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {puantajData.map((k, idx) => {
                                                const durum = getDurumBadge(k.durum);
                                                return (
                                                    <tr key={k.id} className={clsx("border-b border-border-light", idx % 2 === 0 ? 'bg-bg-secondary' : 'bg-bg-primary')}>
                                                        <td className="py-3 px-4 text-sm font-medium text-text-primary">
                                                            {new Date(k.tarih).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', weekday: 'short' })}
                                                        </td>
                                                        <td className="py-3 px-4 text-center"><StatusBadge variant={durum.color} status={durum.label} /></td>
                                                        <td className="py-3 px-4 text-center text-sm text-text-secondary">{k.baslangic && k.bitis ? `${k.baslangic} - ${k.bitis}` : '-'}</td>
                                                        <td className="py-3 px-4 text-center text-sm font-medium text-text-primary">{k.calismaSaat > 0 ? `${k.calismaSaat.toFixed(1)} sa` : '-'}</td>
                                                        <td className="py-3 px-4 text-center">
                                                            <span className={clsx("text-sm font-medium", k.fazlaMesai > 0 ? 'text-warning' : 'text-text-muted')}>
                                                                {k.fazlaMesai > 0 ? `+${k.fazlaMesai.toFixed(1)} sa` : '-'}
                                                            </span>
                                                        </td>
                                                        <td className="py-3 px-4 text-sm text-text-muted max-w-[200px] truncate">{k.not || '-'}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        ) : (
                            <div className="text-center py-8">
                                <Clock size={40} className="mx-auto text-text-muted mb-3" />
                                <p className="text-text-secondary">Henüz puantaj kaydı bulunmuyor</p>
                            </div>
                        )}
                    </Card>
                );
            })()}
            {/* Belgeler Sekmesi */}
            {activeTab === 'belgeler' && (
                <Card padding="lg">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                        <h3 className="text-lg font-semibold text-text-primary flex items-center gap-2">
                            <Paperclip size={20} />
                            Personel Belgeleri
                            <span className="text-sm font-normal text-text-tertiary">({documents.length})</span>
                        </h3>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Upload Area */}
                        <div className="lg:col-span-1">
                            <DocumentUpload
                                onUpload={(file) => {
                                    setDocuments(prev => [...prev, {
                                        id: `doc-${Date.now()}`,
                                        name: file.name,
                                        type: file.type,
                                        size: file.size,
                                        date: new Date().toISOString().split('T')[0],
                                        category: 'Diğer'
                                    }]);
                                }}
                                title="Belge Yükle"
                                description="TC kimlik, SGK belgesi, işe giriş formu..."
                                maxSizeMB={10}
                            />
                        </div>

                        {/* Documents List */}
                        <div className="lg:col-span-2">
                            {documents.length > 0 ? (
                                <div className="space-y-2">
                                    {documents.map(doc => (
                                        <div key={doc.id} className="flex items-center gap-3 p-3 bg-bg-tertiary rounded-lg border border-border-light">
                                            <div className="w-9 h-9 rounded-lg bg-bg-secondary flex items-center justify-center shrink-0">
                                                <FileText size={16} className={doc.type?.includes('pdf') ? 'text-error' : 'text-primary'} />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-text-primary truncate">{doc.name}</p>
                                                <p className="text-xs text-text-muted">{doc.category} • {new Date(doc.date).toLocaleDateString('tr-TR')}</p>
                                            </div>
                                            <div className="flex items-center gap-1 shrink-0">
                                                <button className="p-1.5 text-text-muted hover:text-primary rounded-lg hover:bg-bg-hover transition-colors" title="İndir">
                                                    <Download size={14} />
                                                </button>
                                                <button
                                                    onClick={() => setDocuments(prev => prev.filter(d => d.id !== doc.id))}
                                                    className="p-1.5 text-text-muted hover:text-error rounded-lg hover:bg-bg-hover transition-colors"
                                                    title="Sil"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="h-full min-h-[200px] flex items-center justify-center border-2 border-dashed border-border-light rounded-xl">
                                    <p className="text-text-tertiary text-sm">Henüz belge yüklenmemiş.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </Card>
            )}

            {/* Delete Modal */}
            <ConfirmModal
                isOpen={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onConfirm={confirmDelete}
                title="Personeli Sil"
                message={`"${person.name}" personelini silmek istediğinize emin misiniz?`}
                confirmText="Evet, Sil"
                variant="danger"
            />
        </div>
    );
}

// Info Card Component
function InfoCard({ icon: Icon, label, value, className }) {
    return (
        <div className={clsx("flex items-center gap-3 p-3 bg-bg-tertiary rounded-lg", className)}>
            <div className="w-10 h-10 rounded-lg bg-bg-secondary flex items-center justify-center shrink-0">
                <Icon size={18} className="text-text-tertiary" />
            </div>
            <div className="min-w-0">
                <p className="text-xs text-text-muted">{label}</p>
                <p className="font-medium text-text-primary truncate">{value}</p>
            </div>
        </div>
    );
}
